import React, { useState, useEffect } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toggle from '@amzn/meridian/toggle';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import Pagination from '@amzn/meridian/pagination';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './ViewStyle.module.css';
import { TrendAndDetailsMetrics } from './types';
import { SortDirection } from '../../../../Types/SortDirection';
import { getMetricStringIdAndDefaultMessage } from '../LighthouseMetrics/helper';
import { DisplayMetric } from './DisplayMetric';
import { TrendCategory } from '@amzn/aet-galactus-kit';
import {
  computeColumnWidthAsPercent,
  convertToPercent,
  convertToTime,
  findDateLabel,
  getDateSelectionColumnHeader,
  getTeamViewSubtitleMessage,
  sortMetric,
} from './helper';
import {
  MetricBooleanMap,
  PERCENTAGE_TABLE,
} from '../../../Dashboard/DashboardComponents/LighthouseMetrics/helper';

export interface Trend {
  readonly name: string;
  readonly trendDatapoint: TrendDatapoint[];
}

export interface TrendDatapoint {
  readonly timestamp: string;
  readonly datapoint: string | null;
  readonly networkAverage: string | null;
}

interface TeamViewProps {
  dateSelection: string;
  trendMetrics: TrendAndDetailsMetrics;
  metricType: string[];
  subMetricType: string[];
  metricCategory: string;
  customDate: boolean;
}

const itemsPerPage = 10;
const TeamView: React.FC<TeamViewProps> = ({
  dateSelection,
  trendMetrics,
  metricType,
  subMetricType,
  metricCategory,
  customDate,
}): React.ReactElement => {
  const intl = useIntl();
  const [tableData, setTableData] = useState<
    Record<string, Record<string, string>>
  >({});
  const [sortColumn, setSortColumn] = useState('day');
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    SortDirection.ASCENDING
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [timestamps, setTimestamps] = useState<string[]>();
  const [metricTypes, setMetricTypes] = useState<string[]>();

  const [checked, setChecked] = useState(true);

  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;

  const onSort = ({
    sortColumn,
    sortDirection,
  }: {
    sortColumn: string;
    sortDirection: SortDirection;
  }) => {
    setSortDirection(sortDirection);
    setSortColumn(sortColumn);
  };

  useEffect(() => {
    if (!trendMetrics || !trendMetrics.metricDatas) return;

    const newTimestampsSet = new Set<string>();
    // eslint-disable-next-line
    trendMetrics.metricDatas?.trend?.forEach((metric: Trend) => {
      metric.trendDatapoint.forEach((dp: TrendDatapoint) => {
        newTimestampsSet.add(dp.timestamp);
      });
    });
    const timestamps: string[] = Array.from(newTimestampsSet).sort();
    setTimestamps(timestamps);
    setTotalPages(Math.ceil(timestamps.length / itemsPerPage));
    setMetricTypes(trendMetrics.metricTypes);
    if (!trendMetrics.metricDatas || !trendMetrics.metricDatas.trend) return;

    const dataPerTimestamp: {
      [timestamp: string]: { [metricName: string]: string };
    } = {};
    let isPercentage = false;
    if (trendMetrics.metricDatas.trend.length > 0) {
      isPercentage =
        PERCENTAGE_TABLE[
          trendMetrics.metricDatas?.trend[0].name as keyof MetricBooleanMap
        ];
    }
    // eslint-disable-next-line
    trendMetrics.metricDatas?.trend?.forEach((metric: Trend) => {
      const metricName = metric.name;
      metric.trendDatapoint.forEach(dp => {
        const timestamp = dp.timestamp;
        const datapointValue = dp.datapoint;
        const networkAverage = dp.networkAverage;
        if (!dataPerTimestamp[timestamp]) {
          dataPerTimestamp[timestamp] = {};
        }
        if (isPercentage) {
          dataPerTimestamp[timestamp][metricName] =
            convertToPercent(datapointValue);
          dataPerTimestamp[timestamp][`${metricName}NetAvg`] =
            convertToPercent(networkAverage);
        } else {
          dataPerTimestamp[timestamp][metricName] =
            convertToTime(datapointValue);
          dataPerTimestamp[timestamp][`${metricName}NetAvg`] =
            convertToTime(networkAverage);
        }
      });
    });

    if (sortColumn === 'Day') {
      timestamps.sort((a: string, b: string) => {
        return sortDirection === SortDirection.ASCENDING
          ? a.localeCompare(b)
          : b.localeCompare(a);
      });
    } else {
      sortMetric(timestamps, dataPerTimestamp, sortColumn, sortDirection);
    }

    setTimestamps(timestamps);
    setTableData(dataPerTimestamp);
  }, [
    sortDirection,
    sortColumn,
    checked,
    trendMetrics,
    metricType,
    subMetricType,
  ]);

  return (
    <>
      <Row spacingInset='300'>
        {metricCategory === TrendCategory.COMPLIANCE ? (
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_metrics_team_compliance_metrics'
              defaultMessage='Team Compliance Metrics'
            />
          </Text>
        ) : metricCategory === TrendCategory.OTHER ? (
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_metrics_team_other_metrics'
              defaultMessage='Team Other Metrics'
            />
          </Text>
        ) : metricCategory === TrendCategory.EFFICIENCY ? (
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_metrics_team_efficiency_metrics'
              defaultMessage='Team Efficiency Metrics'
            />
          </Text>
        ) : metricCategory === TrendCategory.QUALITY ? (
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_metrics_team_quality_metrics'
              defaultMessage='Team Quality Metrics'
            />
          </Text>
        ) : (
          <Text type='h200'>
            <FormattedMessage
              id='cs_perfportal_nothing_to_show'
              defaultMessage='Nothing to show yet.'
            />
          </Text>
        )}
      </Row>
      <Row width='100%' widths='fill' spacingInset='300'>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Text type='b200'>
              {getTeamViewSubtitleMessage(dateSelection, customDate)}
            </Text>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div>
              {' '}
              <Text type='b300'>
                <FormattedMessage
                  id='cs_perfportal_network_benchmark'
                  defaultMessage='Network Benchmark'
                />{' '}
              </Text>
            </div>
            <div>
              {' '}
              <Toggle checked={checked} onChange={setChecked}>
                {checked}
              </Toggle>
            </div>
          </div>
        </div>
      </Row>
      <Column
        width='100%'
        alignmentHorizontal='end'
        spacing='300'
        spacingInset='300'
      >
        <Table
          headerRows={1}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
          showDividers={true}
          spacing='small'
        >
          <TableRow>
            <TableCell sortColumn='Day' width='15%' alignmentVertical='bottom'>
              {customDate
                ? 'Custom Range'
                : getDateSelectionColumnHeader(dateSelection)}
            </TableCell>
            {metricTypes?.map(metric => (
              <TableCell
                sortColumn={metric}
                alignmentVertical='bottom'
                alignmentHorizontal='center'
                key={metric}
                width={computeColumnWidthAsPercent(metricTypes?.length)}
              >
                <FormattedMessage
                  {...getMetricStringIdAndDefaultMessage(metric)}
                />
              </TableCell>
            ))}
          </TableRow>
          {timestamps
            ?.slice(firstVisibleIndex, lastVisibleIndex)
            .map(timestamp => (
              <TableRow key={timestamp}>
                <TableCell>
                  <Text type='b300'>
                    {customDate ? timestamp : findDateLabel(timestamp)}
                  </Text>
                  {checked && (
                    <>
                      <Text type='b200' color='secondary'>
                        Network Benchmark
                      </Text>
                    </>
                  )}
                </TableCell>
                {metricTypes?.map(metric => (
                  <TableCell key={metric}>
                    <DisplayMetric
                      metric={tableData[timestamp]?.[metric]}
                      metricType={metric}
                      textType='b300'
                    ></DisplayMetric>
                    {checked && (
                      <>
                        <DisplayMetric
                          metric={tableData[timestamp]?.[`${metric}NetAvg`]}
                          metricType={metric}
                          textType='b200'
                          styleName={styles.subdued}
                        ></DisplayMetric>
                      </>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </Table>
        <Pagination
          showSkipArrows={true}
          numberOfPages={totalPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      </Column>
    </>
  );
};

export default TeamView;
